import jsPDF from 'jspdf';

export const generateInvoicePDF = (booking) => {
  // Only generate invoice for confirmed/completed bookings
  if (!['confirmed', 'completed'].includes(booking.status)) {
    console.warn('Invoice can only be generated for confirmed bookings');
    return;
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Colors
  const primaryColor = [31, 58, 104]; // #1F3A68
  const secondaryColor = [203, 59, 59]; // #CB3B3B
  
  // Header
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', 20, 25);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Ariv', pageWidth - 60, 15);
  doc.text('Short-Term Rental Platform', pageWidth - 60, 22);
  
  // Invoice Details
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  
  const invoiceNumber = `INV-${booking.bookingRef || booking._id?.substring(0, 8).toUpperCase() || 'XXXXXX'}`;
  const invoiceDate = booking.confirmedAt 
    ? new Date(booking.confirmedAt).toLocaleDateString('en-GB') 
    : new Date().toLocaleDateString('en-GB');
  
  doc.text(`Invoice Number: ${invoiceNumber}`, 20, 55);
  doc.text(`Invoice Date: ${invoiceDate}`, 20, 62);
  doc.text(`Booking Reference: ${booking.bookingRef || booking._id || 'N/A'}`, 20, 69);
  doc.text(`Payment Status: ${booking.paymentStatus?.toUpperCase() || 'CONFIRMED'}`, 120, 69);
  
  // Property Details
  doc.setFillColor(245, 245, 245);
  doc.rect(15, 80, pageWidth - 30, 35, 'F');
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Property Details', 20, 90);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Property: ${booking.propertyTitle || 'N/A'}`, 20, 98);
  doc.text(`Location: ${booking.propertyLocation || 'N/A'}`, 20, 105);
  doc.text(`Host: ${booking.hostName || 'N/A'}`, 20, 112);
  
  // Guest Details
  doc.setFillColor(245, 245, 245);
  doc.rect(15, 120, pageWidth - 30, 30, 'F');
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Guest Details', 20, 130);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Name: ${booking.guestName || 'N/A'}`, 20, 138);
  doc.text(`Email: ${booking.guestEmail || 'N/A'}`, 20, 145);
  
  // Booking Details
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Booking Details', 20, 165);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Check-in: ${booking.checkIn || 'N/A'}`, 20, 175);
  doc.text(`Check-out: ${booking.checkOut || 'N/A'}`, 100, 175);
  doc.text(`Number of Nights: ${booking.nights || 'N/A'}`, 20, 182);
  doc.text(`Number of Guests: ${booking.numberOfGuests || 'N/A'}`, 100, 182);
  
  // Line Items Table
  const startY = 200;
  
  // Table Header
  doc.setFillColor(...primaryColor);
  doc.rect(15, startY, pageWidth - 30, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Description', 20, startY + 7);
  doc.text('Amount', pageWidth - 45, startY + 7);
  
  // Table Rows
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  
  const pricing = booking.pricing || {};
  let currentY = startY + 17;
  
  // Show if price overrides applied
  if (pricing.hasOverrides) {
    doc.setFontSize(8);
    doc.setTextColor(139, 92, 246); // violet
    doc.text('* Special pricing applied for selected dates', 20, currentY);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    currentY += 7;
  }
  
  // Accommodation
  const nightlyDisplay = pricing.hasOverrides 
    ? `Accommodation (${booking.nights || 0} nights - variable rates)`
    : `Accommodation (${booking.nights || 0} nights × £${pricing.nightlyRate || 0})`;
  doc.text(nightlyDisplay, 20, currentY);
  doc.text(`£${(pricing.accommodation || 0).toFixed(2)}`, pageWidth - 45, currentY);
  
  // Cleaning Fee
  currentY += 10;
  doc.text('Cleaning Fee', 20, currentY);
  doc.text(`£${(pricing.cleaningFee || 0).toFixed(2)}`, pageWidth - 45, currentY);
  
  // Service Fee
  currentY += 10;
  doc.text('Service Fee', 20, currentY);
  doc.text(`£${(pricing.serviceFee || 0).toFixed(2)}`, pageWidth - 45, currentY);
  
  // Subtotal line
  currentY += 5;
  doc.setDrawColor(200, 200, 200);
  doc.line(15, currentY, pageWidth - 15, currentY);
  
  // Subtotal
  currentY += 10;
  doc.setFont('helvetica', 'bold');
  doc.text('Subtotal', 20, currentY);
  const subtotal = pricing.subtotal || (pricing.accommodation + pricing.cleaningFee + pricing.serviceFee) || 0;
  doc.text(`£${subtotal.toFixed(2)}`, pageWidth - 45, currentY);
  
  // VAT — only render the row when there's actual VAT to show. Brick &
  // Capital Ltd isn't VAT-registered yet (see backend/tax_config.py), so
  // for every booking right now `pricing.vat` is £0 and showing a "VAT
  // (20%)  £0.00" line is misleading. Once VAT_ENABLED flips to True the
  // ledger will start populating pricing.vat with the real amount and
  // the row reappears automatically.
  const vat = pricing.vat || 0;
  if (vat > 0) {
    currentY += 10;
    doc.setFont('helvetica', 'normal');
    const vatRate = pricing.vatRate || 20;
    doc.text(`VAT (${vatRate}%)`, 20, currentY);
    doc.text(`£${vat.toFixed(2)}`, pageWidth - 45, currentY);
  }

  // Total line
  currentY += 5;
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.5);
  doc.line(15, currentY, pageWidth - 15, currentY);

  // TOTAL banner
  currentY += 12;
  doc.setFillColor(...secondaryColor);
  doc.rect(15, currentY - 7, pageWidth - 30, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL', 20, currentY + 2);
  const total = pricing.total || (subtotal + vat);
  doc.text(`£${total.toFixed(2)}`, pageWidth - 50, currentY + 2);

  // Track the actual bottom of the TOTAL banner so everything below stays
  // anchored to it. Previously the footer text was hardcoded at y=275/280
  // while the banner was dynamic — short invoices ended up with the
  // banner's bottom edge clipping into the footer copy.
  const totalBannerBottom = currentY + 8;

  // Payment Info
  let bodyY = totalBannerBottom + 12;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  if (booking.stripePaymentIntentId) {
    doc.text(`Payment Reference: ${booking.stripePaymentIntentId}`, 20, bodyY);
    bodyY += 8;
  }

  // Footer — anchored to the banner, never to a fixed page position. Drop
  // it to near the bottom of A4 (~297mm) when the content is short, or
  // immediately after the payment info when the content runs long.
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = Math.max(bodyY + 8, pageHeight - 18);
  doc.setTextColor(128, 128, 128);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Thank you for your booking with Ariv.', pageWidth / 2, footerY, { align: 'center' });
  doc.text('For questions about this invoice, please contact your host.', pageWidth / 2, footerY + 5, { align: 'center' });
  
  // Save
  doc.save(`Invoice_${invoiceNumber}.pdf`);
};
